// Home.jsx
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroSection from '../components/Hero';
import AnalysisReport from '../components/About';
import Footer from '../components/Footer';
import Subscription from '../components/Subscriptions';
import { Zap, Building2, Ruler, Database, ShieldCheck, Cpu } from 'lucide-react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const TrustBadge = ({ icon: Icon, title, desc, id, x, y }) => (
  <motion.div 
    variants={{
      hidden: { opacity: 0, scale: 0.9, y: 30 },
      show: { opacity: 1, scale: 1, y: 0 }
    }}
    className="group relative"
  >
    <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
    <div className="relative p-6 rounded-2xl bg-slate-900/50 border border-slate-800 backdrop-blur-md hover:border-blue-500/50 transition-all duration-300 overflow-hidden">
      {/* Scanning Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
          <Icon size={24} strokeWidth={1.5} />
        </div>
        <div className="text-right">
          <span className="block text-[10px] font-mono text-slate-500 uppercase tracking-tighter">Protocol: {id}</span>
        </div>
      </div>

      <h3 className="text-white font-bold text-lg mb-2 tracking-tight group-hover:text-blue-300">
        {title}
      </h3>
      <p className="text-slate-500 text-xs leading-relaxed mb-4">
        {desc}
      </p>

      {/* Decorative Bitmaps */}
      <div className="flex gap-1 mb-4 h-1 items-end">
        {[...Array(12)].map((_, i) => (
          <div 
            key={i} 
            className="w-full bg-slate-800 rounded-full overflow-hidden"
            style={{ height: `${Math.random() * 100}%` }}
          >
             <motion.div 
               animate={{ opacity: [0.3, 1, 0.3] }}
               transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.1 }}
               className="w-full h-full bg-blue-500/40"
             />
          </div>
        ))}
      </div>

      {/* Internal Scan Line */}
      <motion.div 
        animate={{ y: [-100, 300] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 left-0 w-full h-px bg-blue-400/20 blur-[1px] pointer-events-none"
      />
    </div>
  </motion.div>
);

const Home = () => {
  const containerRef = React.useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 30, stiffness: 200 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), springConfig);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Handle hash-based scrolling on location change
  React.useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Navigation */}
      <Navbar />

      <section id="home">
        <HeroSection />
      </section>

      {/* Services section */}
      <section id="analysis-report">
        <AnalysisReport />
      </section>

      <section id="subscription-plans">
        <Subscription />
      </section>

      {/* Standard for Professionals Section - Engineered Precision */}
      <section 
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="py-28 bg-slate-950 overflow-hidden relative cursor-crosshair perspective-1000"
      >
         {/* Blueprint Background Layer */}
         <div className="absolute inset-0 pointer-events-none">
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-[0.07]" 
                 style={{ backgroundImage: 'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)', backgroundSize: '50px 50px' }} 
            />
            {/* Moving X/Y Coordinates */}
            <div className="absolute top-10 left-10 text-[10px] font-mono text-blue-500/30">X: 34.0522° N</div>
            <div className="absolute top-10 right-10 text-[10px] font-mono text-blue-500/30">Y: 118.2437° W</div>
            <div className="absolute bottom-10 left-10 text-[10px] font-mono text-blue-500/30 flex items-center gap-2">
               <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
               LIVE_DATASTREAM_ACTIVE
            </div>
         </div>

         <div className="container mx-auto px-6 relative z-10">
            <motion.div 
               style={{ rotateX, rotateY }}
               className="transform-style-3d"
            >
               <div className="text-center mb-24">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-black uppercase tracking-widest mb-6"
                  >
                    <Cpu size={12} className="animate-spin-slow" />
                    Validation Protocols
                  </motion.div>
                  <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tighter">
                    Engineered for <span className="text-blue-500">Unrivaled</span> Precision.
                  </h2>
                  <p className="text-slate-500 max-w-2xl mx-auto text-lg leading-relaxed font-medium">
                    Our platform executes hundreds of complex geometric checks per analysis, adhering to the highest professional standards in the industry.
                  </p>
               </div>

               <motion.div 
                  variants={{
                     hidden: { opacity: 0 },
                     show: {
                        opacity: 1,
                        transition: { staggerChildren: 0.15 }
                     }
                  }}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-100px" }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
               >
                  <TrustBadge 
                    id="RE-802"
                    icon={Building2} 
                    title="Real Estate Boards" 
                    desc="Direct-access integration with California MLS and municipal zoning data for live property intelligence."
                  />
                  <TrustBadge 
                    id="AIA-441"
                    icon={Ruler} 
                    title="Architectural Standards" 
                    desc="Automated testing modules programmed to AIA standards for rigorous preliminary site validation."
                  />
                  <TrustBadge 
                    id="GIS-909"
                    icon={Database} 
                    title="Geospatial Intelligence" 
                    desc="High-precision GIS processing of fragmented state land registries and municipal endpoints."
                  />
                  <TrustBadge 
                    id="DEV-112"
                    icon={ShieldCheck} 
                    title="Institutional Integrity" 
                    desc="Advanced due diligence protocols utilized by tier-1 developers and equity partners across the state."
                  />
               </motion.div>
            </motion.div>
         </div>

         {/* Side Scanners */}
         <motion.div 
           animate={{ x: [-100, 100], opacity: [0, 0.5, 0] }}
           transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
           className="absolute top-1/4 right-0 w-64 h-[500px] bg-gradient-to-l from-blue-500/10 to-transparent blur-3xl"
         />
      </section>

      {/* Final Call to Action - High Impact */}
      <section className="relative overflow-hidden bg-slate-950 py-24">
         <div className="container mx-auto px-6 relative z-10">
            <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800/80 backdrop-blur-3xl rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl shadow-blue-900/20">
               {/* Decorative Glows */}
               <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
               <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />
               
               <div className="relative z-10 flex flex-col items-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-black uppercase tracking-widest mb-8">
                     <Zap size={14} className="animate-pulse" />
                     Instant Feasibility
                  </div>
                  <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight leading-[1.1]">
                     Ready to unlock your <br />
                     <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-400">
                        property’s true potential?
                     </span>
                  </h2>
                  <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-12 font-medium leading-relaxed">
                     Join the Realtors, Architects, and Developers who are making data-driven decisions every single day.
                  </p>
                  <button 
                     onClick={() => navigate('/dashboard')}
                     className="px-10 py-5 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300 active:translate-y-0 text-xl flex items-center gap-3 border border-blue-400/20 group"
                  >
                     Start Your First Analysis
                     <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <p className="mt-10 text-slate-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                     <ShieldCheck size={14} className="text-slate-400" /> Free Prototype Access • No Credit Card Required
                  </p>
               </div>
            </div>
         </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;